import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import dayjs from 'dayjs';
import {
  Radio, BarChart3, Calendar, Check, ChevronLeft, HelpCircle, X, Sparkles,
} from 'lucide-react';
import { track } from '../utils/track';
import { ShareSuccessModal } from '../components/ShareSuccessModal';

interface ChannelPreview {
  id: string;
  title: string;
  username?: string;
  subscribers: number;
}

interface ChannelStats {
  subscribers: number;
  er: number;
  reach: number;
  lastPostAt: string | null;
}

const STEPS = [
  { idx: 1, key: 'onboarding.step1.title', icon: Radio },
  { idx: 2, key: 'onboarding.step2.title', icon: BarChart3 },
  { idx: 3, key: 'onboarding.step3.title', icon: Calendar },
] as const;

export function OnboardingWizard(): JSX.Element {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [, setSkipped] = useState(false);
  const [botToken, setBotToken] = useState('');
  const [channel, setChannel] = useState<ChannelPreview | null>(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showShare, setShowShare] = useState(false);

  // Step 3 state
  const [postText, setPostText] = useState('');
  const [publishAt, setPublishAt] = useState<string>(
    dayjs().add(1, 'hour').format('YYYY-MM-DDTHH:mm'),
  );
  const [useAi, setUseAi] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');

  const connectMutation = useMutation({
    mutationFn: async (token: string): Promise<ChannelPreview> => {
      const { data } = await api.post<ChannelPreview>('/channels/connect', { bot_token: token });
      return data;
    },
    onSuccess: (data) => {
      setChannel(data);
      toast.success(t('onboarding.step1.connected'));
      // §7a activate — пользователь подключил канал (завершил ключевой шаг онбординга).
      track('activate', { surface: 'onboarding', channel: data.username ?? null });
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { detail?: string } }; message?: string });
      toast.error(msg.response?.data?.detail ?? msg.message ?? t('common.error'));
    },
  });

  const statsQuery = useQuery<ChannelStats>({
    queryKey: ['channel-stats', channel?.id],
    queryFn: async () => {
      const { data } = await api.get<ChannelStats>(`/channels/${channel?.id}/stats`);
      return data;
    },
    enabled: step === 2 && Boolean(channel?.id),
  });

  const aiMutation = useMutation({
    mutationFn: async (prompt: string): Promise<{ text: string }> => {
      const { data } = await api.post<{ text: string }>('/ai/write', { prompt });
      return data;
    },
    onSuccess: (data) => {
      setPostText(data.text);
      toast.success(t('onboarding.step3.aiDone'));
    },
    onError: () => toast.error(t('common.error')),
  });

  const scheduleMutation = useMutation({
    mutationFn: async (): Promise<{ id: string }> => {
      const { data } = await api.post<{ id: string }>('/scheduler/schedule', {
        channel_id: channel?.id,
        text: postText,
        publish_at: dayjs(publishAt).toISOString(),
      });
      return data;
    },
    onSuccess: async () => {
      try {
        await api.post('/notifications/notify', { template: 'welcome' });
      } catch {
        // non-blocking
      }
      // §7a aha — ПЕРВАЯ реализованная ценность (NSM-событие): первый пост запланирован.
      track('aha', { value_type: 'first_post_published', surface: 'onboarding', artifact_id: channel?.username });
      toast.success(t('onboarding.finishToast'));
      // Виральный момент (петли 3/4) в пик — показываем share/referral, затем на дашборд.
      setShowShare(true);
    },
    onError: () => toast.error(t('common.error')),
  });

  const handleConnect = (e: FormEvent): void => {
    e.preventDefault();
    if (!botToken.trim()) {
      toast.error(t('onboarding.step1.invalid'));
      return;
    }
    connectMutation.mutate(botToken.trim());
  };

  const handleSkip = (): void => {
    setSkipped(true);
    nav('/dashboard');
  };

  const goNext = (): void => {
    if (step === 1 && channel) setStep(2);
    else if (step === 2) setStep(3);
  };

  const goBack = (): void => {
    if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-primary/10 to-slate-50 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header: skip + back */}
        <div className="flex items-center justify-between mb-6">
          {step > 1 ? (
            <button
              type="button"
              onClick={goBack}
              className="flex items-center gap-1 text-sm text-brand-muted hover:text-brand-ink"
              aria-label={t('onboarding.back')}
            >
              <ChevronLeft size={16} />
              {t('onboarding.back')}
            </button>
          ) : (
            <div />
          )}
          <button
            type="button"
            onClick={handleSkip}
            className="text-sm text-brand-muted hover:text-brand-ink"
          >
            {t('onboarding.skip')}
          </button>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-center gap-4 mb-8" data-testid="stepper">
          {STEPS.map((s, i) => {
            const active = s.idx === step;
            const done = s.idx < step;
            const Icon = s.icon;
            return (
              <div key={s.idx} className="flex items-center gap-4">
                <div
                  className={clsx(
                    'flex items-center gap-2 px-3 py-2 rounded-full text-sm transition',
                    active && 'bg-brand-primary text-white',
                    done && 'bg-brand-primary/20 text-brand-primary',
                    !active && !done && 'bg-white text-brand-muted border border-slate-200',
                  )}
                  data-testid={`step-${s.idx}${active ? '-active' : ''}`}
                >
                  {done ? <Check size={16} /> : <Icon size={16} />}
                  <span className="hidden sm:inline">{t(s.key)}</span>
                  <span className="sm:hidden">{s.idx}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={clsx('w-8 h-0.5', done ? 'bg-brand-primary' : 'bg-slate-200')} />
                )}
              </div>
            );
          })}
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <div className="card">
            <h2 className="text-2xl font-bold text-brand-ink mb-2">{t('onboarding.step1.heading')}</h2>
            <p className="text-brand-muted mb-6">{t('onboarding.step1.desc')}</p>
            <form onSubmit={handleConnect} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="bot-token">
                  {t('onboarding.step1.tokenLabel')}
                </label>
                <input
                  id="bot-token"
                  type="text"
                  className="input w-full font-mono text-sm"
                  placeholder="123456:ABC..."
                  value={botToken}
                  onChange={(e) => setBotToken(e.target.value)}
                  disabled={Boolean(channel)}
                />
              </div>
              <button
                type="button"
                onClick={() => setShowHelpModal(true)}
                className="flex items-center gap-1 text-sm text-brand-primary hover:underline"
              >
                <HelpCircle size={14} />
                {t('onboarding.step1.helpLink')}
              </button>
              {!channel && (
                <button
                  type="submit"
                  className="btn-primary w-full"
                  disabled={connectMutation.isPending}
                >
                  {connectMutation.isPending ? t('common.loading') : t('onboarding.step1.connect')}
                </button>
              )}
            </form>

            {channel && (
              <div className="mt-6 p-4 rounded-lg bg-brand-primary/5 border border-brand-primary/20" data-testid="channel-preview">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-brand-primary/20 flex items-center justify-center">
                    <Radio size={20} className="text-brand-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-brand-ink">{channel.title}</div>
                    <div className="text-xs text-brand-muted">
                      {channel.subscribers.toLocaleString()} {t('onboarding.step1.subs')}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={goNext}
                  className="btn-primary w-full mt-4"
                >
                  {t('onboarding.next')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="card">
            <h2 className="text-2xl font-bold text-brand-ink mb-2">{t('onboarding.step2.heading')}</h2>
            <p className="text-brand-muted mb-6">{t('onboarding.step2.baseline')}</p>

            {statsQuery.isLoading && (
              <div className="text-brand-muted">{t('common.loading')}</div>
            )}
            {statsQuery.isError && (
              <div className="text-red-600">{t('common.error')}</div>
            )}
            {statsQuery.data && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-grid">
                <StatCard label={t('onboarding.step2.subscribers')} value={statsQuery.data.subscribers.toLocaleString()} />
                <StatCard label={t('onboarding.step2.er')} value={`${statsQuery.data.er.toFixed(1)}%`} />
                <StatCard label={t('onboarding.step2.reach')} value={statsQuery.data.reach.toLocaleString()} />
                <StatCard
                  label={t('onboarding.step2.lastPost')}
                  value={statsQuery.data.lastPostAt ? dayjs(statsQuery.data.lastPostAt).format('DD MMM') : '—'}
                />
              </div>
            )}

            <button
              type="button"
              onClick={goNext}
              className="btn-primary w-full mt-6"
            >
              {t('onboarding.next')}
            </button>
          </div>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <div className="card">
            <h2 className="text-2xl font-bold text-brand-ink mb-2">{t('onboarding.step3.heading')}</h2>
            <p className="text-brand-muted mb-6">{t('onboarding.step3.desc')}</p>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label htmlFor="use-ai" className="text-sm font-medium flex items-center gap-2">
                  <Sparkles size={16} className="text-brand-primary" />
                  {t('onboarding.step3.useAi')}
                </label>
                <input
                  id="use-ai"
                  type="checkbox"
                  checked={useAi}
                  onChange={(e) => setUseAi(e.target.checked)}
                  className="w-4 h-4"
                />
              </div>

              {useAi && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium" htmlFor="ai-prompt">
                    {t('onboarding.step3.aiPrompt')}
                  </label>
                  <input
                    id="ai-prompt"
                    type="text"
                    className="input w-full"
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={aiMutation.isPending || !aiPrompt.trim()}
                    onClick={() => aiMutation.mutate(aiPrompt.trim())}
                  >
                    {aiMutation.isPending ? t('common.loading') : t('onboarding.step3.aiGenerate')}
                  </button>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="post-text">
                  {t('onboarding.step3.text')}
                </label>
                <textarea
                  id="post-text"
                  rows={5}
                  maxLength={4096}
                  className="input w-full"
                  value={postText}
                  onChange={(e) => setPostText(e.target.value)}
                />
                <div className="text-xs text-brand-muted text-right mt-1">{postText.length}/4096</div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="publish-at">
                  {t('onboarding.step3.publishAt')}
                </label>
                <input
                  id="publish-at"
                  type="datetime-local"
                  className="input w-full"
                  value={publishAt}
                  onChange={(e) => setPublishAt(e.target.value)}
                />
              </div>

              <button
                type="button"
                className="btn-primary w-full"
                disabled={scheduleMutation.isPending || !postText.trim() || !publishAt}
                onClick={() => scheduleMutation.mutate()}
              >
                {scheduleMutation.isPending ? t('common.loading') : t('onboarding.step3.schedule')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bot Token Help Modal */}
      {showHelpModal && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setShowHelpModal(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white rounded-xl max-w-md w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="absolute right-3 top-3 text-brand-muted hover:text-brand-ink"
              onClick={() => setShowHelpModal(false)}
              aria-label={t('common.cancel')}
            >
              <X size={18} />
            </button>
            <h3 className="text-lg font-semibold mb-3">{t('onboarding.step1.helpTitle')}</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-brand-ink">
              <li>{t('onboarding.step1.help1')}</li>
              <li>{t('onboarding.step1.help2')}</li>
              <li>{t('onboarding.step1.help3')}</li>
              <li>{t('onboarding.step1.help4')}</li>
            </ol>
          </div>
        </div>
      )}

      {/* Виральный момент после aha (петли 3/4) */}
      <ShareSuccessModal
        open={showShare}
        onClose={() => nav('/dashboard')}
        surface="onboarding"
        channelHandle={channel?.username}
        subscribers={channel?.subscribers}
      />
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
      <div className="text-xs text-brand-muted">{label}</div>
      <div className="text-lg font-semibold text-brand-ink">{value}</div>
    </div>
  );
}
